<template>
  <v-container fluid>
    <page-header :showBackButton="cid !== null">
      <template slot="topRightSlot" v-if="cid === null">
        <v-btn
          depressed
          elevation="0"
          class="ml-2 mb-4"
          color="primary"
          type="button"
          :to="{ name: 'CreateInvoice' }"
          >{{ $t('menu.createInvoice') }}</v-btn
        >
      </template>
    </page-header>

    <datatable
      :isLoading="tableLoading"
      :tableHeaders="tableHeaders"
      :tableData="tableData"
      :itemTotal="tableTotal"
      :page.sync="tableFilter.page"
      :pageLimit.sync="tableFilter.limit"
      @view-clicked="navDetail($event)"
      :enableSearch="cid === null"
      enableClearAll
      @options-update="getList()"
      searchPlaceholder="orderSearchPlaceholder"
      @search-clicked="search($event)"
      @all-search-cleared="clearSearch()"
    >
      <template slot="custom-toolbar-slot">
        <form-date-picker
          hideDetails
          dense
          :fieldValue.sync="tableFilter.date"
          disableFuture
          class="mr-3 mt-2 mb-3"
          placeholder="searchOrderDate"
          @changed="searchDate($event)"
        />
        <v-checkbox
          hide-details
          v-model="tableFilter.expired_soon"
          :label="$t('expirySoonOrder')"
          color="primary"
          class="colorBlack--text mr-3 mt-2 mb-3"
          @change="searchExpiredSoon()"
        ></v-checkbox>
      </template>
      <template v-slot:[`item.payment_status`]="{ item }">
        <span
          v-if="item.payment_status"
          :class="{ 'success--text': item.payment_status === 'paid', 'error--text': item.payment_status === 'unpaid' }"
          >{{ $t(`paymentStatusOptions.${item.payment_status}`) }}</span
        >
      </template>
      <template v-slot:[`item.company_type`]="{ item }">{{ item.company_type | formatCompanyType }}</template>
    </datatable>
  </v-container>
</template>

<script>
import Datatable from '@/components/Datatable.vue'
import FormSelect from '@/components/form/FormSelect.vue'
import FormDatePicker from '@/components/form/FormDatePicker.vue'
export default {
  name: 'OrderList',
  components: {
    Datatable,
    FormSelect,
    FormDatePicker,
  },
  computed: {
    cid() {
      const id = parseInt(this.$route.params.cid)
      if (!isNaN(id)) {
        return id
      }

      return null
    },
  },
  data: vm => ({
    tableLoading: false,
    tableData: [],
    tableTotal: 0,
    tableFilter: {
      page: 1,
      limit: 10,
      date: '',
      keyword: '',
      expired_soon: false,
    },
    tableHeaders: [
      { value: 'create_date', text: vm.$t('orderDateTime'), width: 130 },
      { value: 'order_id', text: vm.$t('orderId') },
      { value: 'company_type', text: vm.$t('companyType'), width: 110 },
      { value: 'company_name_en', text: vm.$t('companyNameEn'), width: 250 },
      { value: 'company_name_zh', text: vm.$t('companyNameZh'), width: 250 },
      { value: 'service', text: vm.$t('service'), width: 300 },
      { value: 'price', text: vm.$t('price'), prefix: 'HK$' },
      { value: 'service_date', text: vm.$t('serviceValidDate'), width: 230 },
      { value: 'payment_status', text: vm.$t('status'), width: 80 },
      { value: 'actionView', text: '', align: 'end' },
    ],
  }),
  watch: {
    '$route.name': function() {
      this.tableFilter.keyword = ''
      this.tableFilter.date = ''
      this.tableFilter.expired_soon = false
      this.tableFilter.page = 1
      this.getQuery()
      this.getList(false)
    },
  },
  methods: {
    async getList(regenUrl = true) {
      if (regenUrl) {
        this.regenerateURL()
      }

      this.tableLoading = true
      try {
        let payload = {
          filter_page: this.tableFilter.page - 1,
          filter_limit: this.tableFilter.limit,
          filters: [],
        }

        if (this.cid !== null) {
          payload.filters.push({ key: 'company_id', value: this.cid })
        } else {
          if (this.tableFilter.keyword) {
            payload['search'] = this.tableFilter.keyword
          }
        }

        if (this.tableFilter.date) {
          payload.filters.push({ key: 'order_date', value: this.tableFilter.date })
        }

        if (this.tableFilter.expired_soon) {
          payload.filters.push({ key: 'expiry_soon', value: '' })
        }

        const { total, data } = await this.$Fetcher.GetOrdersAndBills(payload)
        this.tableTotal = total
        this.tableData = data.map(item => {
          const services = []

          if (item.plan_data_list) {
            item.plan_data_list.forEach(el => {
              services.push(this.$Formatter.getServiceName(el.name_zh, this.$t(`planTypeOption.${el.type}`)))
            })
          }
          if (item.addon_data_list) {
            item.addon_data_list.forEach(el => {
              services.push(this.$Formatter.getServiceName(el.name_zh))
            })
          }

          const company_data = item.company ? item.company : item.company_data ? item.company_data : null
          return {
            id: `${item.data_type}-${item.id}`,
            create_date: item.create_date,
            order_id: this.$Formatter.getOrderId(item),
            company_type: company_data?.type || '',
            company_name_en: company_data?.name_en || '',
            company_name_zh: company_data?.name_zh || '',
            service: services,
            price: item.data_type === 'bill_data' ? (item.payment_amount || item.amount) : item.subtotal,
            service_date:
              item.start_date && item.end_date
                ? this.$t('to', {
                    date1: item.start_date.substring(0, 10),
                    date2: item.end_date.substring(0, 10),
                  })
                : '',
            payment_id: this.$validate.DataValid(item.payment_id) ? item.payment_id : -1,
            payment_status: item.data_type === 'bill_data' ? item.payment_status : item.payment_data?.status || '',
          }
        })

        await this.getLatestPaymentData()
      } catch (err) {
        this.$common.error(err)
        this.tableData = []
      } finally {
        this.tableLoading = false
      }
    },
    async getLatestPaymentData() {
      try {
        const idList = []
        this.tableData.forEach(el => {
          if (el.payment_id > -1) {
            idList.push(el.payment_id)
          }
        })
        const data = await this.$Fetcher.GetPaymentByIdList(idList)
        this.tableData.forEach(item => {
          const found = data.find(el => el.id === item.payment_id)
          if (found) {
            item.payment_status = found.status
          }
        })
      } catch (err) {
        this.$common.error(err)
      }
    },
    search(value) {
      this.tableFilter.keyword = value
      this.tableFilter.page = 1
      this.getList()
    },
    searchDate(value) {
      this.tableFilter.date = value
      this.tableFilter.page = 1
      this.getList()
    },
    searchExpiredSoon() {
      this.tableFilter.page = 1
      this.getList()
    },
    clearSearch() {
      this.tableFilter.keyword = ''
      this.tableFilter.date = ''
      this.tableFilter.expired_soon = false
      this.tableFilter.page = 1
      this.getList()
    },

    getQuery() {
      const query = this.$route.query
      if (Object.keys(query).length > 0) {
        if (this.$validate.DataValid(query.p) && this.$validate.regexNumber(query.p)) {
          this.tableFilter.page = parseInt(query.p)
        }

        if (this.$validate.DataValid(query.l) && this.$validate.regexNumber(query.l)) {
          this.tableFilter.limit = parseInt(query.l)
        }

        if (this.$validate.DataValid(query.q)) {
          this.tableFilter.keyword = query.q
        }

        if (this.$validate.DataValid(query.d)) {
          this.tableFilter.date = query.d
        }

        if (this.$validate.DataValid(query.expirysoon) && query.expirysoon == 'true') {
          this.tableFilter.expired_soon = true
        }
      }
    },
    regenerateURL() {
      const query = this.$route.query
      let q = {
        p: this.tableFilter.limit !== 10 || this.tableFilter.page !== 1 ? this.tableFilter.page : null,
        l: this.tableFilter.limit !== 10 || this.tableFilter.page !== 1 ? this.tableFilter.limit : null,
        q: this.tableFilter.keyword,
        d: this.tableFilter.date,
        expirysoon: this.tableFilter.expired_soon ? 'true' : null,
      }
      const newQuery = this.$common.diffQuery(q, query)
      if (newQuery) {
        this.$router.replace({ query: newQuery })
      }
    },

    navDetail(id) {
      const parts = id.split('-')
      if (parts[0] === 'bill_data') {
        this.$router.push({ name: 'InvoiceDetail', params: { id: parts[1] } })
      } else {
        this.$router.push({ name: 'OrderDetail', params: { id: parts[1] } })
      }
    },
  },

  async mounted() {
    this.getQuery()
    this.getList(false)
  },
}
</script>
